import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PlusCircle, Target as TargetIcon, Trash2 } from 'lucide-react';
import { assetGroupsApi, targetsApi } from '../lib/api-client';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input, Label } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Wizard, type WizardStep } from '../components/ui/wizard';
import { Badge } from '../components/ui/badge';
import { LoadingState, ErrorState, EmptyState, friendlyErrorMessage } from '../components/ui/states';
import { useToast } from '../components/ui/toast';
import { can } from '../lib/rbac';
import { useAuth } from '../lib/auth-context';
import type { AssetGroup, TargetKind } from '../lib/types';

const KIND_LABEL: Record<TargetKind, string> = {
  domain: 'Domain',
  subdomain: 'Subdomain',
  cidr: 'IP range (CIDR)',
  ip: 'IP address',
  web_app: 'Web application',
  api: 'API',
  internal_host: 'Internal host',
  service: 'Service',
};

function AddTargetWizard({ groups, onDone }: { groups: AssetGroup[]; onDone: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [assetGroupId, setAssetGroupId] = useState(groups[0]?.id ?? '');
  const [kind, setKind] = useState<TargetKind>('domain');
  const [value, setValue] = useState('');
  const [finishing, setFinishing] = useState(false);

  const group = groups.find((g) => g.id === assetGroupId);

  const steps: WizardStep[] = [
    {
      key: 'group',
      title: 'Which asset group does this belong to?',
      isValid: () => Boolean(assetGroupId),
      content: (
        <div className="space-y-1">
          <Label htmlFor="wizard-group">Asset group</Label>
          <Select value={assetGroupId} onValueChange={setAssetGroupId}>
            <SelectTrigger id="wizard-group">
              <SelectValue placeholder="Choose an asset group" />
            </SelectTrigger>
            <SelectContent>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ),
    },
    {
      key: 'target',
      title: 'What is the target?',
      isValid: () => value.trim().length > 0,
      content: (
        <div className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="wizard-kind">Type</Label>
            <Select value={kind} onValueChange={(v) => setKind(v as TargetKind)}>
              <SelectTrigger id="wizard-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.entries(KIND_LABEL) as Array<[TargetKind, string]>).map(([v, label]) => (
                  <SelectItem key={v} value={v}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="wizard-value">Value</Label>
            <Input id="wizard-value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="app.example.com" />
          </div>
        </div>
      ),
    },
    {
      key: 'summary',
      title: 'Review',
      content: (
        <div className="space-y-2 rounded-xl bg-surface-sunken p-4 text-sm">
          <p>
            <span className="text-ink-muted">Asset group:</span> <span className="font-medium text-ink">{group?.name}</span>
          </p>
          <p>
            <span className="text-ink-muted">Type:</span> <span className="font-medium text-ink">{KIND_LABEL[kind]}</span>
          </p>
          <p>
            <span className="text-ink-muted">Value:</span> <span className="font-medium text-ink">{value}</span>
          </p>
          <p className="pt-2 text-xs text-ink-muted">
            Adding a target doesn't start any testing by itself — you'll authorize and set scope for it from an engagement next.
          </p>
        </div>
      ),
    },
  ];

  const handleFinish = async () => {
    setFinishing(true);
    try {
      await assetGroupsApi.addTarget(assetGroupId, { kind, value: value.trim() });
      queryClient.invalidateQueries({ queryKey: ['targets'] });
      toast({ title: 'Target added', description: 'Next, create or open an engagement to authorize and scope it for testing.', variant: 'success' });
      onDone();
    } catch (err) {
      toast({ title: 'Could not add target', description: friendlyErrorMessage(err), variant: 'error' });
    } finally {
      setFinishing(false);
    }
  };

  return <Wizard steps={steps} onCancel={onDone} onFinish={handleFinish} finishLabel="Add target" finishing={finishing} />;
}

export function Targets() {
  const { principal } = useAuth();
  const canWrite = principal?.role === undefined || can(principal.role, 'target:write');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [addOpen, setAddOpen] = useState(false);

  const groupsQuery = useQuery({ queryKey: ['asset-groups'], queryFn: () => assetGroupsApi.list() });
  const groups = useMemo(() => groupsQuery.data?.assetGroups ?? [], [groupsQuery.data]);

  const targetQueries = useQueries({
    queries: groups.map((g) => ({
      queryKey: ['targets', g.id],
      queryFn: () => assetGroupsApi.targets(g.id),
      enabled: groups.length > 0,
    })),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => targetsApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['targets'] });
      toast({ title: 'Target removed', variant: 'success' });
    },
    onError: (err) => toast({ title: 'Could not remove target', description: friendlyErrorMessage(err), variant: 'error' }),
  });

  if (groupsQuery.isLoading) return <LoadingState />;
  if (groupsQuery.isError) return <ErrorState error={groupsQuery.error} onRetry={() => groupsQuery.refetch()} />;

  if (groups.length === 0) {
    return (
      <div>
        <PageHeader title="Targets" description="The domains, IPs, and applications you've authorized for testing." />
        <EmptyState
          icon={<TargetIcon size={32} />}
          title="Create an asset group first"
          description="Targets belong to an asset group (e.g. by zone). Head to Asset Groups to create one, then come back to add targets."
          action={
            <Button asChild variant="outline">
              <Link to="/asset-groups">Go to Asset Groups</Link>
            </Button>
          }
        />
      </div>
    );
  }

  const allEmpty = targetQueries.every((q) => (q.data?.targets.length ?? 0) === 0);

  return (
    <div>
      <PageHeader
        title="Targets"
        description="The domains, IPs, and applications you've authorized for testing."
        action={
          canWrite ? (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button>
                  <PlusCircle size={16} /> Add a target
                </Button>
              </DialogTrigger>
              <DialogContent title="Add a target" description="A few quick steps to register something for testing.">
                <AddTargetWizard groups={groups} onDone={() => setAddOpen(false)} />
              </DialogContent>
            </Dialog>
          ) : undefined
        }
      />

      {allEmpty ? (
        <EmptyState icon={<TargetIcon size={32} />} title="No targets yet" description="Add your first target to get started." />
      ) : (
        <div className="space-y-6">
          {groups.map((group, i) => {
            const targets = targetQueries[i]?.data?.targets ?? [];
            if (targets.length === 0) return null;
            return (
              <div key={group.id}>
                <h2 className="mb-2 text-sm font-medium text-ink-muted">{group.name}</h2>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {targets.map((target) => (
                    <Card key={target.id}>
                      <CardContent className="flex items-center justify-between gap-3 pt-6">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-ink">{target.value}</p>
                          <Badge variant="neutral" className="mt-1">
                            {KIND_LABEL[target.kind]}
                          </Badge>
                        </div>
                        {canWrite ? (
                          <ConfirmDialog
                            trigger={
                              <Button variant="ghost" size="icon" aria-label="Remove target">
                                <Trash2 size={16} className="text-danger" />
                              </Button>
                            }
                            title="Remove this target?"
                            description={`"${target.value}" will no longer be available to authorize or test.`}
                            confirmLabel="Remove"
                            variant="danger"
                            onConfirm={() => removeMutation.mutateAsync(target.id)}
                          />
                        ) : null}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
